import {AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {LoadingService} from '@app/core/services/loading.service';
import {NotificationService} from '@app/core/services/notification.service';
import {ContextoPWA, MobileContextService} from '../../../../core/context/services/mobile-context.service';
import {ErrorHandlerService} from '@core/error-handling/services/error-handler.service';
import {PermissionsService} from '@core/auth/services/permissions.service';
import {DateUtils} from '@shared/date-utils';
import {formatarPercentual, ordenarPorNomeOuChamada} from '@shared/utils';
import { PeriodoSelectOption } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';
import { dataEstaNoPeriodoAvaliativo, formatarDataISO, verificaAlunoEnturmado } from '../../utils/frequencia-calendario.utils';
import { getNomeAluno } from '../../utils/mobile-frequencia-aluno-card.utils';
import { aulaFoiRealizada, montarPayloadAulasRealizadas } from '../../utils/mobile-frequencia-aula-realizada.utils';
import {EnturmacaoFrequencia, MobileFrequenciaAulaService} from '../../services/mobile-frequencia-aula.service';
import {MobileFrequenciaValidationService} from '../../services/mobile-frequencia-validation.service';
import {
  EncerramentoTurma,
  Enturmacao,
  FrequenciaDia,
  MobileFrequenciaService,
  TurmaCompleta
} from '../../services/mobile-frequencia.service';


@Component({
  selector: 'app-mobile-frequencia',
  templateUrl: './mobile-frequencia.component.html',
  styleUrls: ['./mobile-frequencia.component.css']
})
export class MobileFrequenciaComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('alertaMensagem', { read: ElementRef }) alertaMensagem?: ElementRef;
  
  private destroy$ = new Subject<void>();
  private mensagemAnterior = '';
  private jaScrollouParaAlerta = false;
  
  enturmacoes: Enturmacao[] = [];
  enturmacoesAula: any[] = [];
  aulasCabecalhoAula: any[] = [];
  frequenciasAulasData: any[] = [];
  justificativas: Array<{ id: number; descricao: string }> = [];
  salvando = false;

  // Inicializados em carregarDadosIniciais()
  mesAtual!: number;
  anoAtual!: number;
  diaSelecionado: number | null = null;

  tipoOrdenacao: 'alfabetica' | 'numeroChamada' = 'alfabetica';
  direcaoOrdenacao: 'asc' | 'desc' = 'asc';
  periodosAvaliativos: Array<{ id: number; descricao: string; dataInicial: string; dataFinal: string }> = [];
  aulaRealizada = false;
  estaDentroDoPeriodo = false;
  periodoDropdownAberto = false;
  calendarioDropdownAberto = false;

  /**
   * Estado do calendário ao abrir o dropdown (ano/mês/dia).
   * Usado para restaurar se o usuário navegar no mês sem escolher dia e fechar (toque fora ou botão).
   */
  private calendarioDropdownSnapshot: { ano: number; mes: number; dia: number | null } | null = null;
  ordenacaoDropdownAberto = false;
  mesDropdownAberto = false;
  abonosModalAberto = false;
  alunoSelecionadoParaAbonos: Enturmacao | null = null;
  abonosJustificativasCount: Map<number, number> = new Map();
  mesesDisponiveis: Array<{ numero: number; nome: string; temDiasLetivos: boolean }> = [];
  carregandoMeses = false;

  encerramentosTurma: EncerramentoTurma[] = [];
  diasLetivosDoMes: string[] = []; // Formato YYYY-MM-DD, vindo do backend

  frequenciasDias: FrequenciaDia[] = [];
  aulasRealizadasDias: any[] = [];
  diasMesLetivos: Array<{
    data: string; // YYYY-MM-DD
    dia: number;
    diaSemana: string; // SEG, TER, QUA, etc
    temAula: boolean;
  }> = [];

  mensagem = '';
  semCalendario = false;
  semConfiguracaoFrequencia = false;
  semContexto = false;

  contexto: ContextoPWA | null = null;
  turmaCompleta?: TurmaCompleta;
  periodoAtual: { id: number; descricao: string; dataInicial: string; dataFinal: string } | null = null;
  carregandoDadosTurma = false;
  carregandoDadosIniciais = false; // Suprime alerta até a conclusão do carregamento
  alterandoFrequencia = new Map<number, boolean>(); // Loading por aluno (enturmacaoId -> isLoading)
  carregandoListaAlunos = false;
  /** True após carregarEnturmacoesAula() concluir (sucesso ou erro). Impede que o filho
   *  exiba "Nenhum aluno encontrado" antes da API responder (race condition com Promise.all). */
  enturmacoesAulaCarregadas = false;
  private frequenciaDiaRequestSeq = 0; // Evita resposta antiga sobrescrever dia atual

  tipoTelaFrequencia: 'dia' | 'aula' | null = null;
  configuracaoFrequencia: any | null = null;
  canOpenAbonoJustificativa = false;

  constructor(
    private frequenciaService: MobileFrequenciaService,
    private frequenciaAulaService: MobileFrequenciaAulaService,
    private contextService: MobileContextService,
    private errorHandler: ErrorHandlerService,
    private validationService: MobileFrequenciaValidationService,
    private notificationService: NotificationService,
    private loadingService: LoadingService,
    private permissionsService: PermissionsService
  ) {}

  async ngOnInit() {
    const contextoInicial = this.contextService.getContextoAtual();
    if (contextoInicial) {
      this.contexto = contextoInicial;
      this.semContexto = false;
      this.refreshAbonoJustificativaPermission();

      // Encerramentos e dados iniciais são independentes — disparados em paralelo
      if (contextoInicial.turma?.id) {
        this.frequenciaService.getEncerramentosTurma(contextoInicial.turma.id)
          .then(enc => { this.encerramentosTurma = enc; })
          .catch(() => { this.encerramentosTurma = []; });
      }

      this.carregarDadosIniciais();
    } else {
      this.semContexto = true;
      this.refreshAbonoJustificativaPermission();
      this.tipoTelaFrequencia = 'dia';
      this.enturmacoes = [];
      this.carregandoListaAlunos = false;
      try {
        window.dispatchEvent(new CustomEvent('abrirModalContexto'));
      } catch {
        // Best-effort — sem listener, só não abre o modal
      }
    }

    let turmaIdAnterior = this.contexto?.turma?.id;
    this.contextService.contexto$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(async contexto => {
      this.contexto = contexto;
      this.refreshAbonoJustificativaPermission();
      if (contexto && contexto.turma && contexto.turma.id) {
        this.semContexto = false;
        const turmaIdAtual = contexto.turma.id;
        if (turmaIdAnterior !== turmaIdAtual) {
          turmaIdAnterior = turmaIdAtual;

          this.frequenciaService.getEncerramentosTurma(turmaIdAtual)
            .then(enc => {
              this.encerramentosTurma = enc;
            })
            .catch(() => { this.encerramentosTurma = []; });

          this.carregarDadosIniciais();
        }
      } else if (!contexto || !contexto.turma) {
        this.semContexto = true;
        this.tipoTelaFrequencia = 'dia';
        this.enturmacoes = [];
        this.carregandoListaAlunos = false;
        turmaIdAnterior = undefined;
      }
    });
    // debounceTime(150ms) evita múltiplos recarregamentos consecutivos ao trocar contexto via modal
    this.contextService.contextChanged$.pipe(
      debounceTime(150),
      takeUntil(this.destroy$)
    ).subscribe(async (novoContexto) => {
      await this.onContextoAlterado(novoContexto);
    });
  }
  
  private async carregarQuadroHorario(): Promise<void> {
    const itemEducacionalIds = (this.contexto?.componentes || []).map((componente) => componente.id);

    if (this.periodoAtual?.id && itemEducacionalIds.length && this.contexto?.turma?.id) {
      try {
        await this.frequenciaService.getDiasQuadroHorario(
          this.contexto.turma.id,
          this.periodoAtual.id,
          itemEducacionalIds
        );
      } catch (error) {
        // Quadro de horário é best-effort — falha aqui não bloqueia o resto do fluxo
      }
    }
  }
  
  /**
   * Carrega configuração da turma para decidir tipo de tela (dia ou aula)
   */
  private async carregarConfiguracaoTurma(): Promise<void> {
    if (!this.contexto?.turma?.id) {
      this.tipoTelaFrequencia = 'dia';
      return;
    }

    this.semConfiguracaoFrequencia = false;
    this.configuracaoFrequencia = await this.frequenciaService.buscarConfiguracaoFrequenciaTurma(this.contexto.turma.id);

    if (!this.configuracaoFrequencia) {
      this.semConfiguracaoFrequencia = true;
      this.tipoTelaFrequencia = 'dia';
      return;
    }

    if (this.configuracaoFrequencia.tipoRegistroFrequencia === 'TOTAL_FALTAS_AULAS') {
      this.tipoTelaFrequencia = 'aula';
    } else {
      this.tipoTelaFrequencia = 'dia';
    }
  }
  
  /**
   * Gera datas de referência para cada semana do mês
   * O endpoint /cabecalho retorna apenas UMA SEMANA por chamada
   * Precisamos gerar múltiplas datas (uma por semana) para cobrir o mês inteiro
   * 
   * @param ano Ano do calendário (ex: 2025)
   * @param mes Mês do calendário, base 0 (0=jan, 1=fev, ...)
   * @param dataInicioPeriodo Data inicial do período avaliativo (yyyy-mm-dd)
   * @param dataFimPeriodo Data final do período avaliativo (yyyy-mm-dd)
   * @returns Array de datas em formato yyyy-mm-dd
   */
  private gerarDatasReferenciaSemanais(
    ano: number, 
    mes: number, 
    dataInicioPeriodo: string,
    dataFimPeriodo: string
  ): string[] {
    const datasReferencia: string[] = [];
    // parseDateISO evita bug de timezone: new Date("YYYY-MM-DD") interpreta como UTC meia-noite
    // e em UTC-3 desloca o dia para o anterior via getDate().
    const parsedInicio = DateUtils.parseDateISO(dataInicioPeriodo);
    const parsedFim = DateUtils.parseDateISO(dataFimPeriodo);
    const inicioPeriodo = parsedInicio
      ? new Date(parsedInicio.ano, parsedInicio.mes, parsedInicio.dia)
      : new Date(dataInicioPeriodo);
    const fimPeriodo = parsedFim
      ? new Date(parsedFim.ano, parsedFim.mes, parsedFim.dia)
      : new Date(dataFimPeriodo);

    const primeiroDiaMes = new Date(ano, mes, 1);
    const ultimoDiaMes = new Date(ano, mes + 1, 0); // Dia 0 do próximo mês = último dia do mês atual

    const dataInicio = primeiroDiaMes < inicioPeriodo ? inicioPeriodo : primeiroDiaMes;
    const dataFim = ultimoDiaMes > fimPeriodo ? fimPeriodo : ultimoDiaMes;

    if (dataInicio > dataFim) {
      return [];
    }

    let dataAtual = new Date(dataInicio);

    while (dataAtual <= dataFim) {
      const dataFormatada = DateUtils.formatarDataISO(dataAtual);
      datasReferencia.push(dataFormatada);
      dataAtual = new Date(dataAtual);
      dataAtual.setDate(dataAtual.getDate() + 7);
    }

    return datasReferencia;
  }
  
  /**
   * Busca dias letivos de um mês sem alterar anoAtual/mesAtual (evita o calendário “pular” de mês durante a resolução do dia inicial).
   *
   * FREQUÊNCIA DIÁRIA: /api/frequencia/turma/.../dias
   * FREQUÊNCIA POR AULA: cabecalho por semana agregado
   */
  private async buscarDiasLetivosParaMes(
    ano: number,
    mes0: number,
    exibirMensagemSemAulas = false
  ): Promise<string[]> {
    if (this.periodoAtual?.id == null && !(this.isTurmaAtividade() || this.isEjaModular())) {
      return [];
    }
    if (!this.periodoAtual) {
      return [];
    }

    try {
      if (this.tipoTelaFrequencia === 'aula') {
        if (!this.contexto?.turma?.id || !this.contexto?.componentes?.length) {
          return [];
        }

        const itemEducacionalIds = this.contexto.componentes.map(c => c.id);
        const isTurmaAtividade = this.isTurmaAtividade();
        const isEjaModular = this.turmaCompleta?.etapaMatriz?.modular || false;
        const periodoId = this.periodoAtual.id || undefined;

        const datasReferencia = this.gerarDatasReferenciaSemanais(
          ano,
          mes0,
          this.periodoAtual.dataInicial,
          this.periodoAtual.dataFinal
        );

        const turmaId = this.contexto.turma.id;

        const promessas = datasReferencia.map(dataRef =>
          firstValueFrom(
            this.frequenciaAulaService.getAulasDoMes(
              turmaId,
              periodoId,
              itemEducacionalIds,
              dataRef,
              isTurmaAtividade,
              isEjaModular
            )
          ).catch(error => {
            return [];
          })
        );

        const resultadosSemanais = await Promise.all(promessas);
        const todasAulas = resultadosSemanais.flat();
        return [...new Set(todasAulas.map(dia => dia.data))];
      }

      // Para turmas de atividade com período sintético (id=0), getDiasDoMes requer periodoId real
      if (!this.periodoAtual.id) {
        const diasUteis: string[] = [];
        const ultimoDia = new Date(ano, mes0 + 1, 0).getDate();
        for (let d = 1; d <= ultimoDia; d++) {
          const data = new Date(ano, mes0, d);
          const diaSemana = data.getDay();
          if (diaSemana >= 1 && diaSemana <= 5) {
            diasUteis.push(DateUtils.formatarDataISO(data));
          }
        }
        return diasUteis;
      }

      const response = await this.frequenciaService.getDiasDoMes(
        mes0 + 1,
        ano,
        this.periodoAtual.id
      );
      return response?.dias || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Atualiza ano/mês e a lista de dias letivos no mesmo passo, depois que a API responde.
   * Evita o mat-calendar receber mês novo com `diasLetivosDoMes` ainda do mês anterior
   * (dateFilter não casa nenhuma data → todos os dias desabilitados).
   */
  private async aplicarMesEDiasLetivosNoCalendario(
    novoAno: number,
    novoMes: number
  ): Promise<void> {
    await this.carregarQuadroHorario();
    const dias =
      this.periodoAtual?.id != null
        ? await this.buscarDiasLetivosParaMes(novoAno, novoMes, true)
        : [];
    this.anoAtual = novoAno;
    this.mesAtual = novoMes;
    this.diasLetivosDoMes = dias;
  }

  /** Garante que o dia do mês existe no novo mês (ex.: 31 → abril). */
  private ajustarDiaSelecionadoAoMes(ano: number, mes: number): void {
    if (this.diaSelecionado == null) {
      return;
    }
    const ultimoDia = new Date(ano, mes + 1, 0).getDate();
    if (this.diaSelecionado > ultimoDia) {
      this.diaSelecionado = ultimoDia;
    }
  }

  /** FREQUÊNCIA DIÁRIA: /api/frequencia/turma/{turmaId}/dias. FREQUÊNCIA POR AULA: cabecalho por semana. */
  private async carregarDiasLetivosDoMes(): Promise<void> {
    if (!this.periodoAtual) {
      this.diasLetivosDoMes = [];
      return;
    }
    if (this.periodoAtual.id == null && !(this.isTurmaAtividade() || this.isEjaModular())) {
      this.diasLetivosDoMes = [];
      return;
    }

    try {
      if (this.tipoTelaFrequencia === 'aula') {
        if (typeof this.anoAtual === 'undefined' || typeof this.mesAtual === 'undefined' || this.anoAtual === null || this.mesAtual === null) {
          this.diasLetivosDoMes = [];
          return;
        }
      }

      this.diasLetivosDoMes = await this.buscarDiasLetivosParaMes(
        this.anoAtual,
        this.mesAtual,
        true
      );
    } catch (error) {
      this.diasLetivosDoMes = [];
    }
  }

  /**
   * Data de referência para abrir a tela: "hoje" local, limitada ao intervalo do período avaliativo.
   */
  private computarDataReferenciaInicialAbertura(
    periodo: { dataInicial: string; dataFinal: string }
  ): Date {
    const pInicio = DateUtils.parseDateISO(periodo.dataInicial);
    const pFim = DateUtils.parseDateISO(periodo.dataFinal);
    if (!pInicio || !pFim) {
      const hoje = new Date();
      return new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    }
    const inicio = new Date(pInicio.ano, pInicio.mes, pInicio.dia);
    const fim = new Date(pFim.ano, pFim.mes, pFim.dia);
    const hoje = new Date();
    const hojeLocal = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    if (hojeLocal < inicio) {
      return inicio;
    }
    if (hojeLocal > fim) {
      return fim;
    }
    return hojeLocal;
  }

  /**
   * Abre a frequência em: hoje (se for dia letivo no calendário) ou no último dia letivo antes de hoje.
   * Recua o mês quando necessário (ex.: segunda após fim de semana).
   */
  private async resolverDiaSelecionadoHojeOuUltimoLetivoAnterior(): Promise<void> {
    if (!this.periodoAtual) {
      return;
    }

    const dataRef = this.computarDataReferenciaInicialAbertura(this.periodoAtual);
    const anoCorrente = new Date().getFullYear();
    // Período em outro ano civil: abrir no último dia letivo dentro do período (ex.: consultar ano letivo encerrado).
    if (dataRef.getFullYear() !== anoCorrente) {
      await this.resolverUltimoDiaLetivoNoPeriodo();
      return;
    }

    const dataAlvoISO = DateUtils.formatarDataISO(dataRef);
    const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
    if (!parsedInicio) {
      await this.fallbackPrimeiroDiaLetivoDisponivel();
      return;
    }

    const limiteMesInicio = new Date(parsedInicio.ano, parsedInicio.mes, 1);
    let mesCursor = new Date(dataRef.getFullYear(), dataRef.getMonth(), 1);

    for (let iter = 0; iter < 24; iter++) {
      if (mesCursor < limiteMesInicio) {
        break;
      }

      const anoM = mesCursor.getFullYear();
      const mesM = mesCursor.getMonth();
      const diasMes = await this.buscarDiasLetivosParaMes(anoM, mesM);
      const sorted = [...diasMes].sort();
      if (sorted.includes(dataAlvoISO)) {
        const [y, m, d] = dataAlvoISO.split('-').map(Number);
        this.anoAtual = y;
        this.mesAtual = m - 1;
        this.diaSelecionado = d;
        await this.carregarDiasLetivosDoMes();
        return;
      }

      const before = sorted.filter(d => d < dataAlvoISO);
      if (before.length > 0) {
        const melhor = before[before.length - 1];
        const [y, m, d] = melhor.split('-').map(Number);
        this.anoAtual = y;
        this.mesAtual = m - 1;
        this.diaSelecionado = d;
        await this.carregarDiasLetivosDoMes();
        return;
      }

      mesCursor = new Date(mesCursor.getFullYear(), mesCursor.getMonth() - 1, 1);
    }

    await this.fallbackPrimeiroDiaLetivoDisponivel();
  }

  /**
   * Último dia letivo entre dataInicial e dataFinal do período (varre os meses sem alterar o calendário até o fim).
   */
  private async resolverUltimoDiaLetivoNoPeriodo(): Promise<void> {
    if (!this.periodoAtual) {
      return;
    }
    const pIni = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
    const pFim = DateUtils.parseDateISO(this.periodoAtual.dataFinal);
    if (!pIni || !pFim) {
      await this.fallbackPrimeiroDiaLetivoDisponivel();
      return;
    }

    const dataInicioISO = this.periodoAtual.dataInicial.slice(0, 10);
    const dataFimISO = this.periodoAtual.dataFinal.slice(0, 10);

    let mesCursor = new Date(pIni.ano, pIni.mes, 1);
    const mesUltimo = new Date(pFim.ano, pFim.mes, 1);

    let melhorISO: string | null = null;

    while (mesCursor.getTime() <= mesUltimo.getTime()) {
      const anoM = mesCursor.getFullYear();
      const mesM = mesCursor.getMonth();
      const diasMes = await this.buscarDiasLetivosParaMes(anoM, mesM);
      const sorted = [...diasMes].sort();
      const dentro = sorted.filter(d => d >= dataInicioISO && d <= dataFimISO);
      if (dentro.length > 0) {
        const ultimoDoMes = dentro[dentro.length - 1];
        if (!melhorISO || ultimoDoMes > melhorISO) {
          melhorISO = ultimoDoMes;
        }
      }
      mesCursor = new Date(mesCursor.getFullYear(), mesCursor.getMonth() + 1, 1);
    }

    if (melhorISO) {
      const [y, m, d] = melhorISO.split('-').map(Number);
      this.anoAtual = y;
      this.mesAtual = m - 1;
      this.diaSelecionado = d;
      await this.carregarDiasLetivosDoMes();
      return;
    }

    await this.fallbackPrimeiroDiaLetivoDisponivel();
  }

  /**
   * Quando não há dia letivo válido na busca regressiva, usa o primeiro dia letivo a partir do início do período.
   */
  private async fallbackPrimeiroDiaLetivoDisponivel(): Promise<void> {
    if (!this.periodoAtual) {
      return;
    }
    const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
    if (!parsedInicio) {
      return;
    }
    this.anoAtual = parsedInicio.ano;
    this.mesAtual = parsedInicio.mes;
    await this.carregarDiasLetivosDoMes();
    const sorted = [...this.diasLetivosDoMes].sort();
    if (sorted.length > 0) {
      const first = sorted[0];
      const [y, m, d] = first.split('-').map(Number);
      this.anoAtual = y;
      this.mesAtual = m - 1;
      this.diaSelecionado = d;
      return;
    }
    this.diaSelecionado = parsedInicio.dia;
  }
  
  async carregarDadosIniciais() {

    if (!this.contexto?.turma) {
      this.tipoTelaFrequencia = 'dia';
      this.enturmacoes = [];
      this.carregandoListaAlunos = false;
      this.mensagem = 'Nenhum contexto selecionado. Por favor, selecione uma turma.';
      return;
    }

    // Resetar estado antes de cada carregamento — evita falsos positivos de chamadas anteriores
    this.mensagem = '';
    this.semCalendario = false;
    this.semConfiguracaoFrequencia = false;
    this.carregandoDadosIniciais = true;
    this.aulasCabecalhoAula = [];
    this.enturmacoesAula = [];
    this.enturmacoesAulaCarregadas = false;
    this.frequenciasAulasData = [];

    try { // wrapper externo: garante carregandoDadosIniciais = false ao final (via finally)
    try {
      // FASE 1 (paralelo): calendário + períodos + turma completa + configuração são independentes entre si
      const [ periodosAvaliativos, turmaCompleta] = await Promise.all([
        // [1] Períodos avaliativos — crítico, precisamos do resultado
        this.frequenciaService.getPeriodos(this.contexto.turma.id),

        // [2] Turma completa — opcional, falha não bloqueia
        this.frequenciaService.getTurmaCompleta(this.contexto.turma.id)
          .catch(() => ({ id: this.contexto!.turma!.id })),
        
        // [3] Configuração da turma (decisão dia/aula) — fire-and-forget
        this.carregarConfiguracaoTurma().catch(() => undefined)
      ] as const);

      this.periodosAvaliativos = periodosAvaliativos;
      this.turmaCompleta = turmaCompleta;

      if (this.semConfiguracaoFrequencia) {
        return;
      }

      if (!this.periodosAvaliativos || this.periodosAvaliativos.length === 0) {
        const isAtividadeOuModular = this.isTurmaAtividade() || this.isEjaModular();
        if (isAtividadeOuModular && this.contexto?.componentes?.length) {
          // Turma de atividade/modular: usar itemEducacional.dataInicio/dataFim como período
          // Replica AngularJS: app-select-componente-context.directive.js getPeriodo() → verifyPeriodo()
          const componenteId = this.contexto.componentes[0].id;
          try {
            const itemEducacional = await this.frequenciaService.getItemEducacional(componenteId);
            if (itemEducacional?.dataInicio && itemEducacional?.dataFim) {
              // Criar período sintético a partir do itemEducacional
              this.periodosAvaliativos = [{
                id: 0,
                descricao: 'Período da atividade',
                dataInicial: itemEducacional.dataInicio,
                dataFinal: itemEducacional.dataFim
              }];
            } else {
              this.semCalendario = true;
              return;
            }
          } catch (error) {
            this.semCalendario = true;
            return;
          }
        } else {
          this.semCalendario = true;
          return;
        }
      }
      this.semCalendario = false;
      
      // Selecionar período avaliativo atual (que contém a data de hoje)
      const dataAtual = new Date();
      this.periodoAtual = this.periodosAvaliativos.find(periodo => {
        const inicioParsed = DateUtils.parseDateISO(periodo.dataInicial);
        const fimParsed = DateUtils.parseDateISO(periodo.dataFinal);
        if (!inicioParsed || !fimParsed) {
          return false;
        }
        const inicio = new Date(inicioParsed.ano, inicioParsed.mes, inicioParsed.dia);
        const fim = new Date(fimParsed.ano, fimParsed.mes, fimParsed.dia);
        const hojeLocal = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), dataAtual.getDate());
        return hojeLocal >= inicio && hojeLocal <= fim;
      }) || this.periodosAvaliativos[0]; // Se não encontrar, usar o primeiro

      // Mês/ano visíveis: referência = hoje limitado ao período. O DIA só após PASSO 4 (evita flash
      // do último dia do período/fim do mês e depois “pular” para o letivo correto).
      const dataRefAbertura = this.computarDataReferenciaInicialAbertura(this.periodoAtual);
      this.anoAtual = dataRefAbertura.getFullYear();
      this.mesAtual = dataRefAbertura.getMonth();
      this.diaSelecionado = null;
      this.mesesDisponiveis = [];

      // FASE 2: quadro não depende do mês; dias letivos são carregados em resolverDiaSelecionadoHojeOuUltimoLetivoAnterior
      await this.carregarQuadroHorario();
    } catch (error: unknown) {
      const errorObj = error as { error?: { detail?: { message?: string }; message?: string }; message?: string; status?: number };
      const errorMessage = errorObj?.error?.detail?.message 
        || errorObj?.error?.message 
        || errorObj?.message 
        || 'Erro desconhecido';
      
      if (errorObj?.status === 500) {
        this.mensagem = `Erro no servidor ao carregar períodos avaliativos: ${errorMessage}`;
      } else if (errorObj?.status === 403) {
        this.mensagem = 'Você não tem permissão para acessar esta turma. Entre em contato com a secretaria escolar.';
      } else {
        this.mensagem = `Erro ao carregar períodos avaliativos: ${errorMessage}`;
      }
      
      return;
    }
    
    try {
      if (!this.periodoAtual || !this.periodoAtual.dataInicial) {
        this.mensagem = 'Período avaliativo inválido.';
        return;
      }
      
      // PASSO 4: abrir em "hoje" (se letivo no período) ou no último dia letivo antes de hoje
      await this.resolverDiaSelecionadoHojeOuUltimoLetivoAnterior();

      this.verificarDentroDoPeriodo();
      // FASE 3 (paralelo): alunos + grid de frequências do mês são independentes entre si
      const loadPromises = [
        this.carregarFrequencia(),
        this.carregarFrequenciasDoMes(),
      ];
      
      if (this.tipoTelaFrequencia === 'aula') {
        loadPromises.push(this.carregarEnturmacoesAula());
        loadPromises.push(this.carregarDadosFrequenciaAula());
      }
      
      await Promise.all(loadPromises);

    } catch (error: any) {
      if (error?.status === 403) {
        this.mensagem = 'Você não tem permissão para acessar a frequência desta turma. Verifique com a secretaria escolar.';
      } else {
        this.mensagem = 'Erro ao carregar frequência. Verifique sua conexão e autenticação.';
      }
      this.enturmacoes = [];
    }
    } finally {
      this.carregandoDadosIniciais = false;
    }
  }

  /** Chamado quando o usuário altera contexto via modal — replica context-resolver + frequencia.controller do AngularJS. */
  private async onContextoAlterado(novoContexto: ContextoPWA): Promise<void> {
    this.carregandoDadosTurma = true;
    this.mensagem = '';
    this.enturmacoes = [];
    this.aulasCabecalhoAula = [];
    this.enturmacoesAula = [];
    this.enturmacoesAulaCarregadas = false;
    this.frequenciasAulasData = [];
    this.contexto = novoContexto;

    if (!novoContexto?.turma?.id) {
      this.semContexto = true;
      this.tipoTelaFrequencia = 'dia';
      this.enturmacoes = [];
      this.carregandoListaAlunos = false;
      this.mensagem = 'Nenhuma turma selecionada. Por favor, selecione uma turma.';
      this.carregandoDadosTurma = false;
      return;
    }

    this.semContexto = false;

    // Aguarda o app-context global ser atualizado — o backend valida se a turma pertence ao
    // contexto (servidor/ano/estabelecimento) e retorna 404 se chamado antes disso.
    await new Promise(resolve => setTimeout(resolve, 150));

    try {
      this.encerramentosTurma = await this.frequenciaService.getEncerramentosTurma(
        novoContexto.turma.id
      );

      const config = await this.frequenciaService.buscarConfiguracaoFrequenciaTurma(novoContexto.turma.id);
      if (!config) {
        this.semConfiguracaoFrequencia = true;
        this.mensagem = '';
        return;
      }
      this.semConfiguracaoFrequencia = false;

      const validacao = this.validationService.validarConfiguracao(
        config,
        novoContexto.turma
      );

      if (!validacao.valido) {
        this.mensagem = validacao.mensagem || 'Configuração de frequência inválida';
        return;
      }

      await this.carregarDadosIniciais();
    } catch (error: any) {
      const errorMessage = error?.error?.detail?.message
        || error?.error?.message
        || error?.message
        || '';

      if (error?.status === 404 || errorMessage.toLowerCase().includes('não foi encontrado')) {
        this.mensagem = 'Nenhuma configuração de frequência encontrada para a turma informada';
      } else if (error?.status === 403) {
        this.mensagem = 'Você não tem permissão para acessar a frequência desta turma';
      } else {
        this.mensagem = 'Nenhuma configuração de frequência encontrada para a turma informada';
      }
    } finally {
      this.carregandoDadosTurma = false;
    }
  }

  get periodoOptions(): PeriodoSelectOption[] {
    return this.periodosAvaliativos.map((periodo) => ({
      ...periodo,
      encerrado: this.isPeriodoFechado(periodo)
    }));
  }

  onPeriodoOptionSelected(option: PeriodoSelectOption): void {
    const periodo = this.periodosAvaliativos.find((item) => item.id === option.id);
    if (periodo) {
      void this.selecionarPeriodo(periodo);
    }
  }

  async togglePeriodoDropdown(): Promise<void> {
    if (this.calendarioDropdownAberto) {
      await this.restaurarCalendarioSeCancelado();
      this.calendarioDropdownAberto = false;
      this.calendarioDropdownSnapshot = null;
    }
    this.periodoDropdownAberto = !this.periodoDropdownAberto;
    this.ordenacaoDropdownAberto = false;
  }

  async selecionarPeriodo(periodo: { id: number; descricao: string; dataInicial: string; dataFinal: string }) {
    this.loadingService.show('Carregando período...', 'calendar');

    try {
      this.periodoAtual = periodo;
      this.periodoDropdownAberto = false;

      // Lista de meses do dropdown deve refletir o novo período (intervalo de datas + letivos)
      this.mesesDisponiveis = [];

      // Mesma regra da abertura da tela: hoje (se letivo) ou último letivo antes de hoje — não usar só dataInicial (pode ser domingo/não letivo).
      await this.resolverDiaSelecionadoHojeOuUltimoLetivoAnterior();

      await this.gerarMesesDisponiveis();

      this.verificarDentroDoPeriodo();

      if (this.tipoTelaFrequencia === 'aula') {
        await Promise.all([
          this.carregarEnturmacoesAula(),
          this.carregarDadosFrequenciaAula(),
        ]);
      } else {
        this.carregarFrequencia();
      }
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Verifica se um período está fechado/encerrado.
   * Consulta o array encerramentosTurma para verificar o status do encerramento.
   */
  isPeriodoFechado(periodo?: { id: number; descricao: string; dataInicial: string; dataFinal: string }): boolean {
    const periodoVerificar = periodo || this.periodoAtual;
    if (!periodoVerificar) return false;

    const encerramento = this.encerramentosTurma.find(enc => {
      // Para períodos normais (bimestre/trimestre/semestre)
      if (enc.periodoAvaliativo?.id === periodoVerificar.id) {
        return true;
      }
      
      // Para exame final, comparar identificadorExameFinal (se existir)
      if (enc.tipoPeriodo === 'EXAME_FINAL' && 
          enc.identificadorExameFinal && 
          parseInt(enc.identificadorExameFinal) === periodoVerificar.id) {
        return true;
      }
      
      return false;
    });
    
    return encerramento?.status === 'ENCERRADO';
  }

  formatarDataCompleta(): string {
    if (!this.diaSelecionado) return '';
    const dia = String(this.diaSelecionado).padStart(2, '0');
    const mes = String(this.mesAtual + 1).padStart(2, '0');
    return `${dia}/${mes}/${this.anoAtual}`;
  }

  getNomeMes(): string {
    return this.getNomeMesPorNumero(this.mesAtual);
  }

  getNomeMesPorNumero(mes: number): string {
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return meses[mes];
  }

  getDiaSemana(): string {
    if (!this.diaSelecionado) return '';
    const data = new Date(this.anoAtual, this.mesAtual, this.diaSelecionado);
    const diaSemana = data.getDay(); // 0 = Domingo ... 6 = Sábado
    if (diaSemana === 0) return 'Domingo';
    if (diaSemana === 6) return 'Sábado';
    // Segunda (1) → "2ª feira", Terça (2) → "3ª feira" etc.
    return `${diaSemana + 1}ª feira`;
  }

  async mesAnterior() {
    await this.navegarParaDiaLetivo(-1);
  }

  async diaSeguinte() {
    await this.navegarParaDiaLetivo(1);
  }

  /**
   * Navega para o dia letivo anterior (direcao -1) ou seguinte (direcao 1), respeitando
   * dias letivos do mês e o período avaliativo — pode mudar de mês se necessário.
   * Busca até 90 dias na direção informada (limite de segurança).
   */
  private async navegarParaDiaLetivo(direcao: 1 | -1) {
    if (!this.diaSelecionado || !this.periodoAtual) return;

    this.loadingService.show(
      direcao === -1 ? 'Carregando dia anterior...' : 'Carregando próximo dia...',
      'calendar'
    );

    try {
      const dataAtual = new Date(this.anoAtual, this.mesAtual, this.diaSelecionado);

      const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
      const parsedFim = DateUtils.parseDateISO(this.periodoAtual.dataFinal);
      if (!parsedInicio || !parsedFim) {
        return;
      }
      const dataInicioPeriodo = new Date(parsedInicio.ano, parsedInicio.mes, parsedInicio.dia);
      const dataFimPeriodo = new Date(parsedFim.ano, parsedFim.mes, parsedFim.dia);
      const dentroDoLimite = (data: Date) =>
        direcao === -1 ? data >= dataInicioPeriodo : data <= dataFimPeriodo;

      const dataTestada = new Date(dataAtual);
      dataTestada.setDate(dataTestada.getDate() + direcao);

      if (!dentroDoLimite(dataTestada)) {
        return;
      }

      const MAX_TENTATIVAS = 90; // limite de segurança
      let tentativas = 0;

      // Estado LOCAL de busca — a UI (this.mesAtual/anoAtual/diasLetivosDoMes) só é
      // atualizada quando um dia letivo for CONFIRMADO, evitando o efeito de calendário
      // piscando entre meses quando não há dias letivos cadastrados.
      let mesBusca = this.mesAtual;
      let anoBusca = this.anoAtual;
      let diasLetivosBusca = this.diasLetivosDoMes;

      while (tentativas < MAX_TENTATIVAS) {
        tentativas++;

        const mesTestado = dataTestada.getMonth();
        const anoTestado = dataTestada.getFullYear();
        const diaTestado = dataTestada.getDate();

        // Cruzou fronteira de mês: carrega os dias letivos em variável local, sem tocar
        // this.mesAtual/anoAtual ainda (evita CD visual até achar um dia letivo).
        if (mesTestado !== mesBusca || anoTestado !== anoBusca) {
          const nomeMes = this.getNomeMesPorNumero(mesTestado);
          this.loadingService.show(`Carregando dados de ${nomeMes}/${anoTestado}...`, 'calendar');
          mesBusca = mesTestado;
          anoBusca = anoTestado;
          diasLetivosBusca = await this.buscarDiasLetivosParaMes(anoTestado, mesTestado, false);
        }

        const dataStr = formatarDataISO(anoTestado, mesTestado, diaTestado);
        if (diasLetivosBusca.includes(dataStr)) {
          if (mesBusca !== this.mesAtual || anoBusca !== this.anoAtual) {
            await this.carregarQuadroHorario();
            this.anoAtual = anoBusca;
            this.mesAtual = mesBusca;
            this.diasLetivosDoMes = diasLetivosBusca;
            if (this.tipoTelaFrequencia !== 'aula') {
              await this.carregarFrequenciasDoMes();
            }
          }
          this.diaSelecionado = diaTestado;
          this.verificarDentroDoPeriodo();
          if (this.tipoTelaFrequencia === 'aula') {
            await this.carregarDadosFrequenciaAula();
            const dataSelecionada = DateUtils.formatarDataISO(
              new Date(this.anoAtual, this.mesAtual, this.diaSelecionado)
            );
            this.aulaRealizada = aulaFoiRealizada(dataSelecionada, this.tipoTelaFrequencia, this.aulasCabecalhoAula, this.aulasRealizadasDias);
          } else {
            await this.carregarFrequencia();
          }
          return;
        }

        dataTestada.setDate(dataTestada.getDate() + direcao);
        if (!dentroDoLimite(dataTestada)) {
          break;
        }
      }
      // Nenhum dia letivo encontrado — estado de UI nunca foi alterado, nada a restaurar.
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Alterna manualmente entre Presente e Falta
   * Baseado no AngularJS: app-button-falta.directive.js
   * 
   * Lógica:
   * - P → F: POST para criar registro de falta
   * - F → P: DELETE para remover registro de falta
   * - ? → P: Apenas marca checkbox (não persiste)
   * - D (dispensado): Bloqueia qualquer alteração
   */
  async togglePresenca(aluno: Enturmacao) {
    if (!this.aulaRealizada) return;

    if (aluno.dispensado) {
      return;
    }

    if (this.alterandoFrequencia.get(aluno.id)) {
      return;
    }

    this.alterandoFrequencia.set(aluno.id, true);

    // Guarda estado original para reverter em caso de erro
    const faltaOriginal = aluno.falta;
    const presenteOriginal = aluno.presente;

    try {
      if (aluno.presente && !aluno.falta) {
        await this.criarRegistroFalta(aluno);
        aluno.presente = false;
        aluno.falta = true;

        this.notificationService.success({
          title: 'Falta Registrada!',
          message: `${aluno.nome} foi marcado com falta.`
        });
        return;
      }

      if (aluno.falta && aluno.frequenciaId) {
        await this.removerRegistroFalta(aluno);
        aluno.falta = false;
        aluno.presente = true;

        this.notificationService.success({
          title: 'Presença Registrada!',
          message: `${aluno.nome} foi marcado como presente.`
        });
        return;
      }

      // ? → P: apenas marca o checkbox, será salvo no lote
      if (!aluno.presente && !aluno.falta) {
        aluno.presente = true;
      }

    } catch (error: any) {
      // Mensagem de erro é exibida pelo HttpErrorNotificationInterceptor
      aluno.falta = faltaOriginal;
      aluno.presente = presenteOriginal;
    } finally {
      this.alterandoFrequencia.set(aluno.id, false);
    }
  }

  isAlterandoFrequencia(alunoId: number): boolean {
    return this.alterandoFrequencia.get(alunoId) || false;
  }

  abrirAbonosJustificativas(aluno: Enturmacao) {
    if (!this.canOpenAbonoJustificativa) {
      return;
    }
    this.alunoSelecionadoParaAbonos = aluno;
    this.abonosModalAberto = true;
  }

  /** Abre a modal de abono/justificativa no modo AULA, adaptando o formato recebido do filho. */
  abrirAbonosJustificativasAula(enturmacao: EnturmacaoFrequencia): void {
    if (!this.canOpenAbonoJustificativa) {
      return;
    }
    this.alunoSelecionadoParaAbonos = {
      id: enturmacao.id,
      nome: enturmacao.nome,
      nomeSocial: enturmacao.nomeSocial,
    } as EnturmacaoFrequencia;
    this.abonosModalAberto = true;
  }

  fecharAbonosModal() {
    this.abonosModalAberto = false;
    this.alunoSelecionadoParaAbonos = null;
  }

  getTurmaId(): number {
    return this.contexto?.turma?.id || 0;
  }

  getPeriodoId(): number {
    return this.periodoAtual?.id || 0;
  }

  getItemEducacionalIds(): number[] {
    return (this.contexto?.componentes || []).map(c => c.id);
  }

  getIsEjaModular(): boolean {
    return this.isEjaModular();
  }

  getEnturmacaoId(): number {
    return this.alunoSelecionadoParaAbonos?.id || 0;
  }

  getAlunoNome(): string {
    return this.alunoSelecionadoParaAbonos ? getNomeAluno(this.alunoSelecionadoParaAbonos) : '';
  }

  private refreshAbonoJustificativaPermission(): void {
    // checkPermissionForStateName é consistente com a diretiva: permissivo em standalone (sem AngularJS).
    const canAbono = this.permissionsService.checkPermissionForStateName('AbonoFaltaPageMapping');
    const canJustificativa = this.permissionsService.checkPermissionForStateName('JustificativaFaltaPageMapping');
    this.canOpenAbonoJustificativa = canAbono || canJustificativa;
  }

  getTurmaNome(): string {
    return this.contexto?.turma?.descricao || this.turmaCompleta?.descricao || '';
  }

  getTipoFrequencia(): 'DIA' | 'AULA' {
    return this.tipoTelaFrequencia === 'aula' ? 'AULA' : 'DIA';
  }

  getPeriodoDescricao(): string {
    return this.periodoAtual?.descricao || '';
  }

  getAbonosJustificativasCount(aluno: Enturmacao): number {
    return this.abonosJustificativasCount.get(aluno.id) || 0;
  }

  async carregarContadoresAbonosJustificativas() {
    if (!this.periodoAtual?.id) return;

    try {
      const promises = this.enturmacoes.map(async (aluno: Enturmacao) => {
        try {
          const response = await this.frequenciaService.getAbonosJustificativasFaltasDias(
            aluno.id,
            this.periodoAtual!.id
          );
          this.abonosJustificativasCount.set(aluno.id, response.total || 0);
        } catch (error) {
          this.abonosJustificativasCount.set(aluno.id, 0);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      // Best-effort — falha aqui não bloqueia a tela
    }
  }

  /** Callback ao salvar um abono/justificativa: recarrega frequência e contador do aluno. */
  async onAbonoJustificativaSalvo(event: { action: 'create' | 'update' | 'delete'; tipo: 'ABONO' | 'JUSTIFICATIVA' }) {
    if (!this.contexto?.turma?.id || !this.periodoAtual) return;

    try {
      if (this.tipoTelaFrequencia === 'aula') {
        await this.carregarDadosFrequenciaAula();
      } else {
        await this.carregarFrequencia();
      }

      // Recarrega o contador só do aluno selecionado (mais eficiente que tudo de novo)
      if (this.alunoSelecionadoParaAbonos && this.periodoAtual?.id) {
        try {
          const response = await this.frequenciaService.getAbonosJustificativasFaltasDias(
            this.alunoSelecionadoParaAbonos.id,
            this.periodoAtual.id
          );
          this.abonosJustificativasCount.set(this.alunoSelecionadoParaAbonos.id, response.total || 0);
        } catch (error) {
          // Best-effort — mantém o contador anterior em caso de falha
        }
      }

      let titulo = '';
      let mensagem = '';
      let icone = '';
      const isAbono = event.tipo === 'ABONO';

      switch (event.action) {
        case 'create':
          titulo = 'Sucesso';
          mensagem = isAbono ? 'Abono criado com sucesso!' : 'Justificativa criada com sucesso!';
          icone = 'plus-circle';
          break;
        case 'update':
          titulo = 'Atualizado';
          mensagem = isAbono ? 'Abono atualizado com sucesso!' : 'Justificativa atualizada com sucesso!';
          icone = 'edit-2';
          break;
        case 'delete':
          titulo = 'Excluído';
          mensagem = isAbono ? 'Abono excluído com sucesso!' : 'Justificativa excluída com sucesso!';
          icone = 'trash-2';
          break;
      }

      this.notificationService.success({ title: titulo, message: mensagem, icon: icone });
    } catch (error) {
      // Best-effort — o salvamento em si já foi confirmado antes deste recarregamento
    }
  }

  get alunosOrdenados(): Enturmacao[] {
    if (!this.enturmacoes || this.enturmacoes.length === 0) {
      return [];
    }

    return ordenarPorNomeOuChamada(
      this.enturmacoes,
      aluno => aluno.numeroChamada,
      aluno => aluno.nome,
      this.tipoOrdenacao,
      this.direcaoOrdenacao
    );
  }

  alternarOrdenacao(tipo: 'alfabetica' | 'numeroChamada'): void {
    // Se clicar no mesmo tipo, inverte a direção (toggle ASC/DESC)
    if (this.tipoOrdenacao === tipo) {
      this.direcaoOrdenacao = this.direcaoOrdenacao === 'asc' ? 'desc' : 'asc';
    } else {
      // Se mudar o tipo, sempre começa em ASC
      this.tipoOrdenacao = tipo;
      this.direcaoOrdenacao = 'asc';
    }
  }

  async toggleOrdenacaoDropdown(): Promise<void> {
    if (this.calendarioDropdownAberto) {
      await this.restaurarCalendarioSeCancelado();
      this.calendarioDropdownAberto = false;
      this.calendarioDropdownSnapshot = null;
    }
    this.ordenacaoDropdownAberto = !this.ordenacaoDropdownAberto;
    this.periodoDropdownAberto = false;
  }

  selecionarOrdenacao(tipo: 'alfabetica' | 'numeroChamada', direcao: 'asc' | 'desc'): void {
    this.tipoOrdenacao = tipo;
    this.direcaoOrdenacao = direcao;
    this.ordenacaoDropdownAberto = false;
  }

  getIconeOrdenacaoAtual(): string {
    if (this.tipoOrdenacao === 'alfabetica') {
      return this.direcaoOrdenacao === 'asc' ? 'arrow-down-a-z' : 'arrow-down-z-a';
    } else {
      return this.direcaoOrdenacao === 'asc' ? 'arrow-down-0-1' : 'arrow-down-1-0';
    }
  }

  async toggleCalendarioDropdown(): Promise<void> {
    if (this.calendarioDropdownAberto) {
      await this.restaurarCalendarioSeCancelado();
      this.calendarioDropdownAberto = false;
      this.calendarioDropdownSnapshot = null;
    } else {
      this.calendarioDropdownSnapshot = {
        ano: this.anoAtual,
        mes: this.mesAtual,
        dia: this.diaSelecionado
      };
      this.calendarioDropdownAberto = true;
    }
    this.periodoDropdownAberto = false;
    this.ordenacaoDropdownAberto = false;
  }

  async fecharCalendarioDropdownPorOverlay(): Promise<void> {
    await this.restaurarCalendarioSeCancelado();
    this.calendarioDropdownAberto = false;
    this.calendarioDropdownSnapshot = null;
  }

  selecionarDiaCalendario(dia: number): void {
    if (!this.isDiaLetivo(dia)) {
      return;
    }
    this.loadingService.show('Carregando frequências...', 'calendar');
    try {
      this.calendarioDropdownSnapshot = null;
      this.diaSelecionado = dia;
      this.calendarioDropdownAberto = false;
      this.verificarDentroDoPeriodo();
      if (this.tipoTelaFrequencia === 'aula') {
        this.carregarDadosFrequenciaAula();
      } else {
        this.carregarFrequencia();
      }
    } finally {
      setTimeout(() => this.loadingService.hide(), 300);
    }
  }

  async navegarMesAnteriorCalendario(): Promise<void> {
    await this.navegarMesCalendario(-1);
  }

  async navegarMesSeguinteCalendario(): Promise<void> {
    await this.navegarMesCalendario(1);
  }

  /**
   * Navega para o mês anterior (direcao -1) ou seguinte (direcao 1) no calendário dropdown.
   * Recarrega os dias letivos do novo mês.
   */
  private async navegarMesCalendario(direcao: 1 | -1): Promise<void> {
    if (!this.podeNavegarMes(direcao)) return;

    let novoMes = this.mesAtual + direcao;
    let novoAno = this.anoAtual;

    if (novoMes < 0) {
      novoMes = 11;
      novoAno--;
    } else if (novoMes > 11) {
      novoMes = 0;
      novoAno++;
    }

    this.loadingService.show('Atualizando calendário...', 'calendar');

    try {
      await this.aplicarMesEDiasLetivosNoCalendario(novoAno, novoMes);
      if (this.tipoTelaFrequencia === 'aula') {
        this.ajustarDiaSelecionadoAoMes(novoAno, novoMes);
      } else {
        this.diaSelecionado = null;
        this.enturmacoes = [];
        this.carregandoListaAlunos = false;
      }
    } finally {
      this.loadingService.hide();
    }
  }

  podeNavegarMesAnterior(): boolean {
    return this.podeNavegarMes(-1);
  }

  podeNavegarMesSeguinte(): boolean {
    return this.podeNavegarMes(1);
  }

  /** Não permite navegar antes do início nem depois do fim do período avaliativo. */
  private podeNavegarMes(direcao: 1 | -1): boolean {
    if (!this.periodoAtual) return false;

    if (direcao === -1) {
      const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
      if (!parsedInicio) return false;

      let mesAnterior = this.mesAtual - 1;
      let anoAnterior = this.anoAtual;
      if (mesAnterior < 0) {
        mesAnterior = 11;
        anoAnterior--;
      }
      const primeiroDiaMesAnterior = new Date(anoAnterior, mesAnterior, 1);
      return primeiroDiaMesAnterior >= new Date(parsedInicio.ano, parsedInicio.mes, 1);
    }

    const parsedFim = DateUtils.parseDateISO(this.periodoAtual.dataFinal);
    if (!parsedFim) return false;

    let mesSeguinte = this.mesAtual + 1;
    let anoSeguinte = this.anoAtual;
    if (mesSeguinte > 11) {
      mesSeguinte = 0;
      anoSeguinte++;
    }
    const primeiroDiaMesSeguinte = new Date(anoSeguinte, mesSeguinte, 1);
    return primeiroDiaMesSeguinte <= new Date(parsedFim.ano, parsedFim.mes + 1, 0);
  }

  /**
   * Volta para "hoje". Se hoje não pertence ao período atual, tenta achar o período avaliativo
   * que contenha a data de hoje. Se hoje não for letivo, usa a mesma regra da abertura da tela:
   * último dia letivo antes de hoje (não seleciona dia futuro para lançamento de frequência).
   */
  async voltarParaHoje(): Promise<void> {
    if (!this.periodoAtual) {
      return;
    }

    const hoje = new Date();
    const hojeNorm = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

    this.loadingService.show('Indo para a data atual', 'home');

    try {
      let periodoFoiAlterado = false;

      if (!dataEstaNoPeriodoAvaliativo(hojeNorm, this.periodoAtual)) {
        const periodoComHoje = this.periodosAvaliativos.find(p =>
          dataEstaNoPeriodoAvaliativo(hojeNorm, p)
        );
        if (periodoComHoje) {
          this.periodoAtual = periodoComHoje;
          this.mesesDisponiveis = [];
          periodoFoiAlterado = true;
        }
      }

      const periodoAlvo = this.periodoAtual;
      const parsedInicio = DateUtils.parseDateISO(periodoAlvo.dataInicial);
      const parsedFim = DateUtils.parseDateISO(periodoAlvo.dataFinal);
      if (!parsedInicio || !parsedFim) {
        this.calendarioDropdownAberto = false;
        this.calendarioDropdownSnapshot = null;
        return;
      }

      const inicioPeriodo = new Date(parsedInicio.ano, parsedInicio.mes, parsedInicio.dia);
      const fimPeriodo = new Date(parsedFim.ano, parsedFim.mes, parsedFim.dia);

      const dataAlvo =
        hojeNorm < inicioPeriodo
          ? inicioPeriodo
          : hojeNorm > fimPeriodo
            ? fimPeriodo
            : hojeNorm;

      const anoAlvo = dataAlvo.getFullYear();
      const mesAlvo = dataAlvo.getMonth();
      const diaAlvo = dataAlvo.getDate();

      await this.aplicarMesEDiasLetivosNoCalendario(anoAlvo, mesAlvo);

      if (this.isDiaLetivo(diaAlvo)) {
        this.diaSelecionado = diaAlvo;
        this.ajustarDiaSelecionadoAoMes(anoAlvo, mesAlvo);
      } else {
        await this.resolverDiaSelecionadoHojeOuUltimoLetivoAnterior();
        if (this.diaSelecionado != null) {
          this.notificationService.info({
            title: 'Dia não letivo',
            message:
              'Hoje não possui aula ou não é letivo. Foi selecionado o último dia letivo antes da data atual.',
            icon: 'calendar'
          });
        }
      }

      if (periodoFoiAlterado) {
        await this.gerarMesesDisponiveis();
      }

      if (this.diaSelecionado == null) {
        this.verificarDentroDoPeriodo();
        this.calendarioDropdownAberto = false;
        this.calendarioDropdownSnapshot = null;
        if (periodoFoiAlterado) {
          if (this.tipoTelaFrequencia === 'aula') {
            await this.carregarEnturmacoesAula();
            await this.carregarDadosFrequenciaAula();
          } else {
            this.carregarFrequencia();
          }
        }
        return;
      }

      this.verificarDentroDoPeriodo();

      this.calendarioDropdownAberto = false;
      this.calendarioDropdownSnapshot = null;
      if (periodoFoiAlterado && this.tipoTelaFrequencia === 'aula') {
        await this.carregarEnturmacoesAula();
      }
      if (this.tipoTelaFrequencia === 'aula') {
        await this.carregarDadosFrequenciaAula();
      } else {
        this.carregarFrequencia();
      }
    } finally {
      this.loadingService.hide();
    }
  }

  shouldShowDataNaoLetivaEmptyState(): boolean {
    // Só faz sentido quando o backend já retornou ao menos um dia letivo no mês;
    // com lista vazia, qualquer data parece "não letiva" e mascararia turma sem calendário.
    return Boolean(
      this.diaSelecionado &&
      this.diasLetivosDoMes.length > 0 &&
      !this.isDiaLetivo(this.diaSelecionado) &&
      !this.carregandoDadosIniciais &&
      !this.carregandoDadosTurma
    );
  }

  /**
   * Mês carregado sem nenhum dia letivo (API/calendário escolar) — aviso igual ao de turma sem calendário.
   * Diferente de "dia escolhido não letivo" quando há outros dias letivos no mês.
   *
   * Não se aplica ao tipo "aula": para esse tipo, dias vazios significam ausência de quadro de horários
   * (não de calendário). O componente filho app-mobile-frequencia-aula exibe a mensagem correta nesses casos.
   * O calendário sem períodos já é tratado por semCalendario = true antes desta verificação.
   */
  deveExibirAvisoCalendarioSemDiasLetivosNoMes(): boolean {
    return Boolean(
      this.tipoTelaFrequencia !== 'aula' &&
      this.periodoAtual &&
      !this.semConfiguracaoFrequencia &&
      !this.carregandoDadosIniciais &&
      !this.carregandoDadosTurma &&
      this.diasLetivosDoMes.length === 0
    );
  }

  /** Oculta lista, legenda, modo aula e estados de edição (checkbox/P/F). */
  deveOcultarGradeFrequenciaPorCalendario(): boolean {
    return this.shouldShowDataNaoLetivaEmptyState() || this.deveExibirAvisoCalendarioSemDiasLetivosNoMes();
  }

  /** Modo dia: existe aluno enturmado na data (não só matrícula fora do intervalo). */
  private temEnturmacaoAtivaNoDia(): boolean {
    return this.enturmacoes.some(a => !a.desabilitado);
  }

  /**
   * Checkbox "aula realizada" e edição de P/F só em dia letivo, com alunos para registrar
   * (modo dia: lista carregada e ao menos um não desabilitado; modo aula: cabeçalho + enturmações).
   */
  shouldShowCheckboxAulaRealizada(): boolean {
    if (this.diaSelecionado == null || !this.isDiaLetivo(this.diaSelecionado)) {
      return false;
    }
    if (this.tipoTelaFrequencia === 'aula') {
      return (
        this.aulasCabecalhoAula.length > 0 &&
        this.enturmacoesAula.length > 0
      );
    }
    if (this.carregandoListaAlunos) {
      return false;
    }
    return this.temEnturmacaoAtivaNoDia();
  }

  /**
   * Modo dia: mês exibido no calendário, mas nenhum dia letivo escolhido (após trocar mês ou cancelar).
   */
  shouldShowSelecioneDiaLetivoEmptyState(): boolean {
    if (this.tipoTelaFrequencia !== 'dia') {
      return false;
    }
    return Boolean(
      this.diaSelecionado === null &&
      !this.semContexto &&
      !this.mensagem &&
      !this.semCalendario &&
      !this.semConfiguracaoFrequencia &&
      !this.carregandoDadosIniciais &&
      !this.carregandoDadosTurma
    );
  }

  /**
   * Exibe a linha com data completa e dia da semana no cabeçalho do calendário.
   */
  exibirCalendarioDataCompleta(): boolean {
    if (this.diaSelecionado == null) {
      return false;
    }
    if (this.tipoTelaFrequencia === 'aula') {
      return true;
    }
    return this.isDiaLetivo(this.diaSelecionado);
  }

  private async restaurarCalendarioSeCancelado(): Promise<void> {
    const snap = this.calendarioDropdownSnapshot;
    if (!snap || this.diaSelecionado !== null) {
      return;
    }
    const precisaMes = snap.ano !== this.anoAtual || snap.mes !== this.mesAtual;
    if (precisaMes) {
      await this.aplicarMesEDiasLetivosNoCalendario(snap.ano, snap.mes);
    }
    this.diaSelecionado = snap.dia;
    this.verificarDentroDoPeriodo();
    if (this.tipoTelaFrequencia === 'aula') {
      await this.carregarDadosFrequenciaAula();
    } else {
      this.carregarFrequencia();
    }
  }

  async toggleMesDropdown(): Promise<void> {
    if (this.mesDropdownAberto) {
      this.mesDropdownAberto = false;
      return;
    }

    this.mesDropdownAberto = true;

    if (this.mesesDisponiveis.length === 0) {
      this.carregandoMeses = true;

      try {
        await this.gerarMesesDisponiveis();
      } finally {
        this.carregandoMeses = false;
      }
    }
  }

  private async gerarMesesDisponiveis(): Promise<void> {
    const nomesMeses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    if (!this.periodoAtual) {
      this.mesesDisponiveis = nomesMeses.map((nome, index) => ({
        numero: index,
        nome,
        temDiasLetivos: false
      }));
      return;
    }

    const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
    const parsedFim = DateUtils.parseDateISO(this.periodoAtual.dataFinal);
    const anoInicio = parsedInicio?.ano ?? this.anoAtual;
    const anoFim = parsedFim?.ano ?? this.anoAtual;
    const mesInicio = anoInicio === this.anoAtual ? (parsedInicio?.mes ?? 0) : 0;
    const mesFim = anoFim === this.anoAtual ? (parsedFim?.mes ?? 11) : 11;

    const mesesParaVerificar: number[] = [];
    for (let mes = mesInicio; mes <= mesFim; mes++) {
      mesesParaVerificar.push(mes);
    }

    const verificacoes = await Promise.all(
      mesesParaVerificar.map(mes => this.verificarMesTemDiasLetivos(this.anoAtual, mes))
    );

    this.mesesDisponiveis = mesesParaVerificar.map((mes, index) => ({
      numero: mes,
      nome: nomesMeses[mes],
      temDiasLetivos: verificacoes[index]
    }));
  }

  private async verificarMesTemDiasLetivos(ano: number, mes: number): Promise<boolean> {
    // Turma de atividade/modular com período sintético (id=0):
    // O WEB AngularJS não verifica dias letivos por mês — todos os meses
    // dentro do range dataInicial/dataFinal são considerados ativos.
    if (this.periodoAtual && !this.periodoAtual.id && (this.isTurmaAtividade() || this.isEjaModular())) {
      return true;
    }

    if (this.periodoAtual?.id == null) return false;

    try {
      const diasDoMes = await this.frequenciaService.getDiasDoMes(
        mes + 1, // API espera 1-12 (mes é 0-11 do JS)
        ano,
        this.periodoAtual.id
      );
      return diasDoMes.dias && diasDoMes.dias.length > 0;
    } catch (error) {
      return false;
    }
  }

  async selecionarMes(mes: number): Promise<void> {
    if (mes === this.mesAtual) {
      this.mesDropdownAberto = false;
      return;
    }

    try {
      this.loadingService.show('Carregando mês...', 'calendar');

      this.diaSelecionado = null;
      await this.aplicarMesEDiasLetivosNoCalendario(this.anoAtual, mes);
      this.verificarDentroDoPeriodo();

      this.mesDropdownAberto = false;
      this.carregarFrequencia();
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Verifica se um dia é letivo consultando o array diasLetivosDoMes.
   * O backend já valida: período avaliativo, quadro de horários e calendário.
   */
  isDiaLetivo(dia: number): boolean {
    if (!this.diasLetivosDoMes || this.diasLetivosDoMes.length === 0) {
      return false;
    }
    const dataStr = formatarDataISO(this.anoAtual, this.mesAtual, dia);
    return this.diasLetivosDoMes.includes(dataStr);
  }

  /** "Dentro do período" = há qualquer interseção entre o mês navegado e o período avaliativo. */
  verificarDentroDoPeriodo() {
    if (!this.periodoAtual) {
      this.estaDentroDoPeriodo = false;
      return;
    }

    const parsedInicio = DateUtils.parseDateISO(this.periodoAtual.dataInicial);
    const parsedFim = DateUtils.parseDateISO(this.periodoAtual.dataFinal);
    if (!parsedInicio || !parsedFim) {
      this.estaDentroDoPeriodo = false;
      return;
    }
    const inicioPeriodo = new Date(parsedInicio.ano, parsedInicio.mes, parsedInicio.dia);
    const fimPeriodo = new Date(parsedFim.ano, parsedFim.mes, parsedFim.dia);

    const inicioMes = new Date(this.anoAtual, this.mesAtual, 1);
    const ultimoDiaMes = new Date(this.anoAtual, this.mesAtual + 1, 0);

    this.estaDentroDoPeriodo = inicioMes <= fimPeriodo && ultimoDiaMes >= inicioPeriodo;
  }

  async onAulaRealizadaChange(aulaRealizada: boolean) {
    this.aulaRealizada = aulaRealizada;

    if (this.aulaRealizada) {
      // MODO AULA: marca todas as aulas do quadro como realizadas
      if (this.tipoTelaFrequencia === 'aula') {
        if (!this.contexto?.turma || !this.periodoAtual || !this.diaSelecionado) {
          this.mensagem = 'Selecione uma turma, período e dia para registrar as aulas.';
          this.aulaRealizada = false;
          return;
        }

        const dataSelecionada = DateUtils.formatarDataISO(
          new Date(this.anoAtual, this.mesAtual, this.diaSelecionado)
        );

        const aulasDoDia = this.aulasCabecalhoAula
          .filter(dia => dia.data === dataSelecionada)
          .flatMap(dia => dia.aulas);

        this.loadingService.show('Registrando presenças...', 'check-circle');

        try {
          const aulasPayload = montarPayloadAulasRealizadas(
            aulasDoDia,
            dataSelecionada,
            true,
            this.isTurmaAtividade(),
            this.turmaCompleta?.etapaMatriz?.modular || false,
            this.contexto!.turma!.id,
            this.periodoAtual!.id
          );

          const response = await firstValueFrom(
            this.frequenciaAulaService.marcarAulasRealizadas(aulasPayload)
          );

          response.forEach((aulaAtualizada: any) => {
            const diaAlocacao = this.aulasCabecalhoAula.find(dia => dia.data === aulaAtualizada.data);
            if (diaAlocacao) {
              const aulaEncontrada = diaAlocacao.aulas.find((a: any) => a.numero === aulaAtualizada.numero);
              if (aulaEncontrada) {
                aulaEncontrada.id = aulaAtualizada.id;
                aulaEncontrada.realizada = aulaAtualizada.realizada;
              }
            }
          });

          await this.carregarDadosFrequenciaAula();

          this.notificationService.success({
            title: 'Presenças registradas!',
            message:
              'A presença foi atribuída a todos os alunos na data selecionada.'
          });

        } catch (error: any) {
          this.aulaRealizada = false;
        } finally {
          this.loadingService.hide();
        }

        return;
      }

      // MODO DIA: marca o dia inteiro como aula realizada
      if (!this.contexto?.turma || !this.periodoAtual || !this.diaSelecionado) {
        this.mensagem = 'Selecione uma turma, período e dia para registrar a aula.';
        this.aulaRealizada = false;
        return;
      }

      const dataSelecionada = DateUtils.formatarDataISO(
        new Date(this.anoAtual, this.mesAtual, this.diaSelecionado)
      );

      this.loadingService.show('Registrando presença...', 'check-circle');

      try {
        // Payload igual ao AngularJS — sem itemEducacional
        const payload = {
          data: dataSelecionada,
          turma: { id: this.contexto.turma.id },
          periodoAvaliativo: { id: this.periodoAtual.id }
        };

        const aulaRealizadaResponse = await this.frequenciaService.saveAulaRealizada(payload);

        // Marca presente só quem não tem falta nem dispensa (preserva F e D)
        this.enturmacoes.forEach(enturmacao => {
          if (!enturmacao.falta && !enturmacao.dispensado) {
            enturmacao.presente = true;
          }
        });

        await this.carregarFrequencia();
        this.notificationService.success({
          title: 'Presença Registrada!',
          message: 'Todos os alunos foram marcados como presentes.'
        });

      } catch (error: any) {
        this.aulaRealizada = false;
      } finally {
        this.loadingService.hide();
      }
    } else {
      // Desmarcou: remover aula realizada
      if (!this.diaSelecionado) {
        this.mensagem = 'Nenhum dia selecionado para remover aula realizada.';
        this.aulaRealizada = true;
        return;
      }

      const dataSelecionada = DateUtils.formatarDataISO(
        new Date(this.anoAtual, this.mesAtual, this.diaSelecionado)
      );

      // MODO AULA: desmarcar todas as aulas do quadro
      if (this.tipoTelaFrequencia === 'aula') {
        const aulasDoDia = this.aulasCabecalhoAula
          .filter(dia => dia.data === dataSelecionada)
          .flatMap(dia => dia.aulas);

        this.loadingService.show('Desmarcando presenças...', 'x-circle');

        try {
          const aulasPayload = montarPayloadAulasRealizadas(
            aulasDoDia,
            dataSelecionada,
            false,
            this.isTurmaAtividade(),
            this.turmaCompleta?.etapaMatriz?.modular || false,
            this.contexto!.turma!.id,
            this.periodoAtual!.id
          );

          const response = await firstValueFrom(
            this.frequenciaAulaService.marcarAulasRealizadas(aulasPayload)
          );

          response.forEach((aulaAtualizada: any) => {
            const diaAlocacao = this.aulasCabecalhoAula.find(dia => dia.data === aulaAtualizada.data);
            if (diaAlocacao) {
              const aulaEncontrada = diaAlocacao.aulas.find((a: any) => a.numero === aulaAtualizada.numero);
              if (aulaEncontrada) {
                aulaEncontrada.realizada = aulaAtualizada.realizada;
              }
            }
          });

          await this.carregarDadosFrequenciaAula();

          this.notificationService.success({
            title: 'Aula Desmarcada!',
            message: 'As aulas foram marcadas como não realizadas e as presenças dos alunos foram removidas.'
          });

        } catch (error: any) {
          this.aulaRealizada = true;
        } finally {
          this.loadingService.hide();
        }

        return;
      }

      // MODO DIA: remover aula realizada do dia
      const aulaRealizadaDia = this.aulasRealizadasDias?.find(
        (a: any) => a.data === dataSelecionada
      );

      if (!aulaRealizadaDia || !aulaRealizadaDia.id) {
        this.mensagem = 'Não foi encontrada aula realizada para este dia.';
        this.aulaRealizada = true;
        return;
      }

      this.loadingService.show('Removendo presença...', 'x-circle');

      try {
        await this.frequenciaService.deleteAulaRealizada(aulaRealizadaDia.id);

        // Remover presença de alunos (preserva F e D)
        this.enturmacoes.forEach(enturmacao => {
          // Só remove presença se tiver P (não mexe em F nem D)
          if (enturmacao.presente && !enturmacao.falta && !enturmacao.dispensado) {
            enturmacao.presente = false;
            enturmacao.presencaRemota = false;
            // Volta para null (exibe "?")
          }
        });

        // Recarregar dados para sincronizar com backend
        await this.carregarFrequencia();

        // Mensagem de sucesso
        this.notificationService.success({
          title: 'Presença Removida!',
          message: 'A aula realizada foi removida e as presenças foram revertidas.'
        });

      } catch (error: any) {
        // Backend retorna: { message: "ERRO", detail: { message: "mensagem específica" } }
        this.mensagem = error?.error?.detail?.message 
          || error?.error?.message 
          || error?.error?.error
          || 'Não foi possível remover a aula realizada.';
        
        this.aulaRealizada = true; // Reverte o checkbox
      } finally {
        this.loadingService.hide();
      }
    }
  }

  async carregarDadosFrequenciaAula(): Promise<void> {
    if (!this.contexto?.turma || !this.periodoAtual || !this.diaSelecionado) {
      return;
    }

    try {
      const itemEducacionalIds = (this.contexto.componentes || []).map(c => c.id);
      const isTurmaAtividade = this.isTurmaAtividade();
      const isEjaModular = this.turmaCompleta?.etapaMatriz?.modular || false;

      const dataSelecionada = `${this.anoAtual}-${String(this.mesAtual + 1).padStart(2, '0')}-${String(this.diaSelecionado).padStart(2, '0')}`;

      const result = await firstValueFrom(
        this.frequenciaAulaService.carregarDadosFrequenciaAula(
          this.contexto.turma.id,
          this.periodoAtual.id,
          itemEducacionalIds,
          dataSelecionada,
          isTurmaAtividade,
          isEjaModular
        )
      );

      this.aulasCabecalhoAula = result.aulas || [];
      this.frequenciasAulasData = result.frequencias || [];

      const frequenciasComAbono = this.frequenciasAulasData.filter((f: any) => f.abonado || f.justificado);

      this.aulaRealizada = aulaFoiRealizada(dataSelecionada, this.tipoTelaFrequencia, this.aulasCabecalhoAula, this.aulasRealizadasDias);

    } catch (error) {
      this.aulasCabecalhoAula = [];
      this.frequenciasAulasData = [];
    }
  }

  /**
   * Carrega enturmações para frequência por aula
   */
  async carregarEnturmacoesAula(): Promise<void> {
    if (!this.contexto?.turma || !this.contexto?.componentes) {
      this.enturmacoesAula = [];
      this.enturmacoesAulaCarregadas = true;
      return;
    }

    try {
      const itemEducacionalIds = this.contexto.componentes.map(c => c.id);
      const isTurmaAtividade = this.isTurmaAtividade();
      const isEjaModular = this.turmaCompleta?.etapaMatriz?.modular || false;

      this.enturmacoesAula = await firstValueFrom(
        this.frequenciaAulaService.getEnturmacoesFrequencia(
          this.contexto.turma.id,
          itemEducacionalIds,
          isTurmaAtividade,
          isEjaModular
        )
      );

    } catch (error) {
      this.enturmacoesAula = [];
    } finally {
      // Sempre sinaliza que a resposta chegou (sucesso ou erro), permitindo
      // que o filho exiba "Nenhum aluno encontrado" somente após este ponto.
      this.enturmacoesAulaCarregadas = true;
    }
  }

  async carregarFrequencia() {
    if (!this.contexto?.turma) {
      this.mensagem = 'Nenhuma turma selecionada. Por favor, selecione uma turma.';
      this.enturmacoes = [];
      return;
    }

    if (!this.periodoAtual) {
      return;
    }

    if (isNaN(this.mesAtual) || isNaN(this.anoAtual)) {
      this.mensagem = 'Erro ao inicializar calendário. Mês ou ano inválido.';
      this.enturmacoes = [];
      return;
    }

    // Ativar loading antes de carregar
    this.carregandoListaAlunos = true;
    const requestSeq = ++this.frequenciaDiaRequestSeq;

    try {
      // PASSO 1: Buscar enturmações (alunos) da turma
      // Para turmas de atividade, passa filtro (atividade = [ids]) como o WEB AngularJS faz
      let enturmacaoFilter: string | undefined;
      if (this.isTurmaAtividade() && this.contexto?.componentes?.length) {
        const ids = this.contexto.componentes.map(c => c.id).join(',');
        enturmacaoFilter = `(atividade = ${ids})`;
      }

      const enturmacoes = await this.frequenciaService.getEnturmacoesFrequencia(
        this.contexto.turma.id,
        enturmacaoFilter
      );
      if (requestSeq !== this.frequenciaDiaRequestSeq) {
        return;
      }
      
      if (!enturmacoes || enturmacoes.length === 0) {
        this.mensagem = '';
        this.enturmacoes = [];
        return;
      }

      // PASSO 2: registros de frequência do mês (API retorna PRESENTE/FALTA/DISPENSADO/PRESENCA_REMOTA).
      // Turmas de atividade com período sintético (id=0) não têm periodoAvaliativo real
      // e o endpoint exige um — frequências iniciam vazias nesse caso.
      let frequenciasPeriodo: FrequenciaDia[] = [];
      const isAtividade = this.isTurmaAtividade() || this.isEjaModular();
      const temPeriodoReal = this.periodoAtual.id && this.periodoAtual.id > 0;
      try {
        if (temPeriodoReal) {

          frequenciasPeriodo = await this.frequenciaService.getFrequenciaPorMesAno(
            this.contexto.turma.id,
            this.periodoAtual.id,
            this.mesAtual + 1,
            this.anoAtual
          );

          this.aulasRealizadasDias = await this.frequenciaService.getAulasRealizadasDia(
            this.contexto.turma.id,
            this.periodoAtual.id,
            this.mesAtual + 1,
            this.anoAtual
          );
        } else {
          frequenciasPeriodo = [];
          this.aulasRealizadasDias = [];
        }
        if (requestSeq !== this.frequenciaDiaRequestSeq) {
          return;
        }

        const dataSelecionada = this.diaSelecionado
          ? DateUtils.formatarDataISO(new Date(this.anoAtual, this.mesAtual, this.diaSelecionado))
          : null;

        if (dataSelecionada) {
          this.aulaRealizada = aulaFoiRealizada(dataSelecionada, this.tipoTelaFrequencia, this.aulasCabecalhoAula, this.aulasRealizadasDias);
        }

      } catch (error: any) {
        frequenciasPeriodo = [];
      }

      // PASSO 3: mapear enturmações e mesclar com frequências salvas
      const dataSelecionada = this.diaSelecionado
        ? DateUtils.formatarDataISO(new Date(this.anoAtual, this.mesAtual, this.diaSelecionado))
        : null;

      const idsEnturmacoes = enturmacoes.map(e => e.id);
      const idsFrequencias = frequenciasPeriodo.map(f => typeof f.enturmacao === 'object' ? f.enturmacao.id : f.enturmacao);
      this.enturmacoes = enturmacoes.map((e: Enturmacao, index: number) => {
        const frequenciaDoDia = dataSelecionada
          ? frequenciasPeriodo.find((f: FrequenciaDia) => {
              const enturmacaoId = typeof f.enturmacao === 'object' ? f.enturmacao.id : f.enturmacao;
              const match = enturmacaoId === e.id && f.data === dataSelecionada;
              return match;
            })
          : null;

        const desabilitado = dataSelecionada ? !verificaAlunoEnturmado(
          dataSelecionada,
          e.dataInicio,
          e.dataFim
        ) : false;

        // Estatísticas vêm do primeiro registro do aluno no período
        const registroPeriodo = frequenciasPeriodo.find((f: FrequenciaDia) => (typeof f.enturmacao === 'object' ? f.enturmacao.id : f.enturmacao) === e.id);
        const resultadoFrequencia = registroPeriodo?.resultadoFrequencia;

        // A API retorna o aluno em estruturas diferentes conforme o endpoint
        const nomeAluno = e.nome
          || e.matricula?.nome
          || e.matricula?.aluno?.nome
          || e.matricula?.aluno?.pessoa?.nome
          || e.aluno?.nome
          || e.enturmacao?.aluno?.nome
          || e.enturmacao?.pessoa?.nome
          || e.pessoa?.nome
          || 'Nome não informado';

        const nomeSocialAluno = e.matricula?.nomeFantasia || e.matricula?.nomeSocial || e.nomeSocial || undefined;
        const numeroMatricula = e.numeroMatricula
          || e.matricula?.numeroMatricula
          || e.aluno?.numeroMatricula
          || e.enturmacao?.aluno?.numeroMatricula
          || '';
        const numeroChamada = e.numeroChamada
          || e.enturmacao?.numeroChamada;
        const dataInicio = e.dataInicio || e.enturmacao?.dataInicio;
        const dataFim = e.dataFim || e.enturmacao?.dataFim;

        // Endpoint /falta-dia/ retorna só faltas: presença = aula realizada sem registro de falta
        // (app-frequencia-por-dia.directive.js do AngularJS)
        const aulaRealizada = dataSelecionada ? aulaFoiRealizada(dataSelecionada, this.tipoTelaFrequencia, this.aulasCabecalhoAula, this.aulasRealizadasDias) : false;
        const temFalta = frequenciaDoDia?.tipo === 'FALTA';
        const temDispensa = frequenciaDoDia?.tipo === 'DISPENSADO';
        const temPresencaRemota = frequenciaDoDia?.tipo === 'PRESENCA_REMOTA';

        return {
          id: e.id,
          nome: nomeAluno,
          nomeSocial: nomeSocialAluno,
          numeroMatricula: numeroMatricula,
          numeroChamada: numeroChamada,
          dataInicio: dataInicio,
          dataFim: dataFim,
          desabilitado: desabilitado,
          presente: aulaRealizada && !temFalta && !temDispensa && !temPresencaRemota,
          falta: temFalta,
          dispensado: temDispensa,
          presencaRemota: temPresencaRemota,
          justificativa: frequenciaDoDia?.justificativa || '',
          frequenciaId: frequenciaDoDia?.id || null,
          frequenciaVersion: frequenciaDoDia?.version || null,
          frequenciaDia: frequenciaDoDia || null, // preserva o registro completo p/ usar o período original ao editar
          totalFaltas: resultadoFrequencia?.faltas || 0,
          frequenciaPercentual: formatarPercentual(resultadoFrequencia?.percentual)
        };
      });

      this.mensagem = '';

      // Estatísticas vêm só do resultadoFrequencia embutido nos registros (sem endpoint separado,
      // como no AngularJS) — sem registros (ex.: 422 fora do período), ficam em 0.
      await this.carregarContadoresAbonosJustificativas();

    } catch (error: any) {
      if (requestSeq !== this.frequenciaDiaRequestSeq) {
        return;
      }

      if (error?.status === 403) {
        this.mensagem = 'Você não tem permissão para acessar os dados desta turma. Verifique com a secretaria escolar.';
      } else if (error?.status === 422) {
        const mensagemBackend = this.errorHandler.extractErrorMessageComplete(error) || 'Erro de validação';
        this.mensagem = mensagemBackend;
      } else {
        this.mensagem = 'Erro ao carregar frequência. Verifique sua conexão e autenticação.';
      }

      this.enturmacoes = [];
    } finally {
      if (requestSeq === this.frequenciaDiaRequestSeq) {
        this.carregandoListaAlunos = false;
      }
    }
  }

  async carregarFrequenciasDoMes() {
    // id=0 é válido para turmas de atividade (período sintético) — verificar pelo objeto, não pelo id
    if (!this.contexto?.turma?.id || !this.periodoAtual) {
      return;
    }

    // Turmas de atividade/modular com período sintético (id=0) não têm períodoAvaliativo real:
    // - getDiasDoMes requer periodoId > 0 → usa dias letivos já carregados em diasLetivosDoMes
    // - getFrequenciaDia requer periodoId real → frequências iniciam vazias
    const temPeriodoReal = this.periodoAtual.id && this.periodoAtual.id > 0;
    const isAtividadeOuModular = this.isTurmaAtividade() || this.isEjaModular();

    if (!temPeriodoReal && isAtividadeOuModular) {
      this.frequenciasDias = [];
      // Reutilizar diasLetivosDoMes já populado por carregarDiasLetivosDoMes()
      const diasSemana = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
      this.diasMesLetivos = this.diasLetivosDoMes.map((diaStr: string) => {
        const parsed = DateUtils.parseDateISO(diaStr);
        const data = parsed ? new Date(parsed.ano, parsed.mes, parsed.dia) : new Date(diaStr);
        return {
          data: diaStr,
          dia: parsed ? parsed.dia : data.getDate(),
          diaSemana: diasSemana[data.getDay()],
          temAula: true
        };
      });
      // Ainda precisa mesclar com enturmações — segue o fluxo abaixo com dados já definidos
    } else if (!temPeriodoReal) {
      return;
    } else {

    try {
      this.frequenciasDias = await this.frequenciaService.getFrequenciaDia(
        this.contexto.turma.id,
        this.periodoAtual.id,
        this.mesAtual + 1, // JavaScript months are 0-indexed
        this.anoAtual
      );

      const diasDoMes = await this.frequenciaService.getDiasDoMes(
        this.mesAtual + 1,
        this.anoAtual,
        this.periodoAtual.id
      );

      this.diasMesLetivos = diasDoMes.dias.map((diaStr: string) => {
        // Usa parseDateISO para evitar bug de timezone: new Date("YYYY-MM-DD") em UTC-3
        // desloca getDate()/getDay() para o dia anterior.
        const parsed = DateUtils.parseDateISO(diaStr);
        const data = parsed ? new Date(parsed.ano, parsed.mes, parsed.dia) : new Date(diaStr);
        const diaSemanaNum = data.getDay();
        const diasSemana = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

        return {
          data: diaStr,
          dia: parsed ? parsed.dia : data.getDate(),
          diaSemana: diasSemana[diaSemanaNum],
          temAula: true
        };
      });

    } catch (error: any) {
      // Não mostrar erro para o usuário, apenas inicializar vazio
      this.frequenciasDias = [];
      this.diasMesLetivos = [];
    }
    } // fim else (tem período real)

    // Mesclar registros de frequência com enturmações (compartilhado pelos dois fluxos)
    if (this.diasMesLetivos.length > 0 && this.enturmacoes.length > 0) {
      let totalRegistrosEncontrados = 0;
      let totalRegistrosNaoEncontrados = 0;

      this.enturmacoes.forEach((enturmacao, indexAluno) => {
        enturmacao.frequenciasDias = this.diasMesLetivos.map(diaInfo => {
          const registroExistente = this.frequenciasDias.find(f => {
            const enturmacaoId = typeof f.enturmacao === 'object' ? f.enturmacao.id : f.enturmacao;
            return enturmacaoId === enturmacao.id && f.data === diaInfo.data;
          });
          if (registroExistente) { totalRegistrosEncontrados++; }
          else { totalRegistrosNaoEncontrados++; }
          // parseDateISO evita o mesmo bug de timezone de gerarDatasReferenciaSemanais
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          const parsedRegistro = DateUtils.parseDateISO(diaInfo.data);
          const dataRegistro = parsedRegistro
            ? new Date(parsedRegistro.ano, parsedRegistro.mes, parsedRegistro.dia)
            : new Date(diaInfo.data);
          const isDataFutura = dataRegistro > hoje;
          const isAlunoEnturmado = verificaAlunoEnturmado(diaInfo.data, enturmacao.dataInicio, enturmacao.dataFim);
          return {
            data: diaInfo.data, enturmacao: enturmacao.id,
            tipo: registroExistente?.tipo || null, $$tipo: registroExistente?.tipo || null,
            $$isChecked: diaInfo.temAula, $$disabled: isDataFutura || !isAlunoEnturmado,
            $$hasDispensa: registroExistente?.tipo === 'DISPENSADO', isEnturmado: isAlunoEnturmado,
            id: registroExistente?.id, justificativa: registroExistente?.justificativa || '',
            version: registroExistente?.version
          };
        });
      });
    }
  }

  /**
   * Cria registro de FALTA (P → F)
   * Baseado no AngularJS: app-frequencia-por-dia.directive.js linha 233-238
   */
  private async criarRegistroFalta(aluno: Enturmacao): Promise<void> {
    if (!this.diaSelecionado || !this.periodoAtual) {
      throw new Error('Dia ou período não selecionado');
    }

    const dataFormatada = DateUtils.formatarDataISO(
      new Date(this.anoAtual, this.mesAtual, this.diaSelecionado)
    );

    const aulaRealizadaDia = this.aulasRealizadasDias?.find(
      (a: any) => a.data === dataFormatada
    );

    if (!aulaRealizadaDia) {
      throw new Error('Aula realizada não encontrada para esta data');
    }

    const periodoId = aluno.frequenciaDia?.periodoAvaliativo?.id || this.periodoAtual.id;

    const payload: any = {
      isEnturmado: true,
      data: dataFormatada,
      enturmacao: { id: aluno.id },
      justificado: false,
      abonado: false,
      periodoAvaliativo: { id: periodoId },
      aulaRealizada: {
        id: aulaRealizadaDia.id,
        data: aulaRealizadaDia.data,
        turma: aulaRealizadaDia.turma,
        periodoAvaliativo: { id: periodoId },
        itemEducacional: aulaRealizadaDia.itemEducacional
      },
      uniqueId: `toggle_${aluno.id}_${Date.now()}`
    };

    // Turmas de Atividades e EJA Modular não devem ter periodoAvaliativo
    // (replica app-frequencia-resultados.directive.js do AngularJS)
    if (this.isTurmaAtividade() || this.isEjaModular()) {
      delete payload.periodoAvaliativo;
      delete payload.aulaRealizada.periodoAvaliativo;
    }

    const resultado = await this.frequenciaService.criarFalta(payload);

    if (resultado?.registroFaltasDia) {
      aluno.frequenciaId = resultado.registroFaltasDia.id;
      aluno.frequenciaVersion = resultado.registroFaltasDia.version;
    }
  }

  private async removerRegistroFalta(aluno: Enturmacao): Promise<void> {
    if (!aluno.frequenciaId) {
      throw new Error('ID da frequência não encontrado');
    }
    await this.frequenciaService.removePorDia(aluno.frequenciaId);

    delete aluno.frequenciaId;
    delete aluno.frequenciaVersion;
  }

  /** Replica FrequenciaUtilsService.isTurmaAtividade() do AngularJS. */
  private isTurmaAtividade(): boolean {
    return (
      this.turmaCompleta?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR' ||
      this.turmaCompleta?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'
    );
  }

  /** Replica FrequenciaUtilsService.isEjaModular() do AngularJS. */
  private isEjaModular(): boolean {
    return Boolean(this.turmaCompleta?.etapaMatriz?.modular);
  }

  ngAfterViewChecked(): void {
    if (this.mensagem && this.mensagem !== this.mensagemAnterior) {
      this.mensagemAnterior = this.mensagem;
      this.jaScrollouParaAlerta = false;
    }

    if (this.mensagem && !this.jaScrollouParaAlerta && this.alertaMensagem?.nativeElement) {
      this.jaScrollouParaAlerta = true;
      setTimeout(() => {
        this.alertaMensagem?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100); // Delay para garantir que o DOM já foi atualizado
    }

    if (!this.mensagem) {
      this.jaScrollouParaAlerta = false;
      this.mensagemAnterior = '';
    }
  }

  getDataSelecionada(): string {
    if (!this.anoAtual || this.mesAtual === undefined || !this.diaSelecionado) {
      return '';
    }
    const ano = this.anoAtual;
    const mes = String(this.mesAtual + 1).padStart(2, '0'); // JS usa 0-11
    const dia = String(this.diaSelecionado).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByMesNumero(index: number, mes: { numero: number }): number {
    return mes.numero ?? index;
  }

  trackByAlunoId(index: number, aluno: Enturmacao): number | string {
    return aluno.id ?? index;
  }
}
